/**
 * UserManagement — super_admin-only page (backend enforces 403).
 * Simple list + create form + inline permission toggles + password reset + disable.
 */
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { ArrowRight, Plus, Trash2, KeyRound, Loader2, ShieldAlert, LogOut } from "lucide-react";
import { usersApi } from "@/lib/api";

const ALL_PERMS = [
  { key: "add_drug", label: "إضافة الأدوية" },
  { key: "edit_drug", label: "تعديل الأدوية" },
  { key: "view_analytics", label: "رؤية الإحصائيات" },
];

const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map(x => x?.msg || "").join(" ");
  return e?.message || "حدث خطأ";
};

export default function UserManagement({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    usersApi.list()
      .then(d => setUsers(d.items || []))
      .catch(e => toast.error(fmtErr(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="user-management-panel" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b-2 border-slate-900 pb-6">
        <div>
          <div className="text-sm font-bold text-slate-500 mb-2">دوائي / لوحة الإدارة / الحساب والأمان</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900">إدارة المستخدمين</h1>
          <p className="text-sm text-slate-500 mt-1">إنشاء وتعديل حسابات الأدمن مع صلاحيات محددة</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="um-add-btn" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-4 py-3 rounded-xl cursor-pointer">
            <Plus className="w-4 h-4" /><span>إضافة مستخدم</span>
          </button>
          <button data-testid="um-back-btn" onClick={onBack}
            className="inline-flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold px-4 py-3 rounded-xl cursor-pointer">
            <ArrowRight className="w-4 h-4" /><span>العودة إلى الحساب والأمان</span>
          </button>
        </div>
      </div>

      {showForm && <UserForm onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
      ) : users.length === 0 ? (
        <div className="text-center py-12 text-slate-500 bg-white border-2 border-dashed border-slate-200 rounded-2xl">
          لا يوجد مستخدمون بعد. اضغط "إضافة مستخدم" للبدء.
        </div>
      ) : (
        <div className="space-y-3">
          {users.map((u) => (
            <UserRow key={u.id} user={u} onChange={load} />
          ))}
        </div>
      )}
    </div>
  );
}

function UserRow({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const isSuper = user.role === "super_admin";

  const togglePerm = async (perm) => {
    if (isSuper) return;
    const next = user.permissions.includes(perm)
      ? user.permissions.filter(p => p !== perm)
      : [...user.permissions, perm];
    setBusy(true);
    try { await usersApi.update(user.id, { permissions: next }); toast.success("تم تحديث الصلاحيات — أُبطلت جلسات المستخدم"); onChange(); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => {
    setBusy(true);
    try { await usersApi.update(user.id, { active: !user.active }); toast.success(user.active ? "تم تعطيل المستخدم" : "تم تفعيل المستخدم"); onChange(); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const resetPw = async () => {
    const pw = window.prompt("كلمة المرور الجديدة (12 حرف على الأقل)");
    if (!pw) return;
    setBusy(true);
    try { await usersApi.setPassword(user.id, pw); toast.success("تم إعادة تعيين كلمة المرور — أُبطلت جلسات المستخدم"); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  const forceLogout = async () => {
    setBusy(true);
    try { await usersApi.logoutAll(user.id); toast.success("تم إنهاء جميع جلسات المستخدم"); }
    catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid={`um-row-${user.username}`} className="bg-white border-2 border-slate-200 rounded-2xl p-5">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <span className="font-bold text-lg text-slate-900" dir="ltr">{user.username}</span>
          {isSuper && <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-slate-900 text-white">Super Admin</span>}
          {!user.active && <span className="text-xs font-bold px-2 py-0.5 rounded-md bg-red-100 text-red-800">معطّل</span>}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {!isSuper && (
            <button data-testid={`um-reset-pw-${user.username}`} disabled={busy} onClick={resetPw}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-md font-bold disabled:opacity-50">
              <KeyRound className="w-3 h-3" />إعادة تعيين كلمة المرور
            </button>
          )}
          {!isSuper && (
            <button data-testid={`um-logout-all-${user.username}`} disabled={busy} onClick={forceLogout}
              className="inline-flex items-center gap-1 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 rounded-md font-bold disabled:opacity-50">
              <LogOut className="w-3 h-3" />إنهاء الجلسات
            </button>
          )}
          {!isSuper && (
            <button data-testid={`um-toggle-active-${user.username}`} disabled={busy} onClick={toggleActive}
              className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-md font-bold disabled:opacity-50 ${user.active ? "bg-red-50 hover:bg-red-100 text-red-800" : "bg-emerald-50 hover:bg-emerald-100 text-emerald-800"}`}>
              {user.active ? "تعطيل" : "تفعيل"}
            </button>
          )}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {ALL_PERMS.map(p => {
          const has = isSuper || user.permissions.includes(p.key);
          return (
            <button key={p.key} type="button" disabled={isSuper || busy}
              data-testid={`um-perm-${p.key}-${user.username}`}
              onClick={() => togglePerm(p.key)}
              className={`text-xs font-bold px-3 py-1.5 rounded-md border-2 transition-colors ${has ? "bg-emerald-50 border-emerald-200 text-emerald-900" : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-400"} ${(isSuper || busy) ? "cursor-not-allowed opacity-80" : "cursor-pointer"}`}>
              {has ? "✓ " : ""}{p.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UserForm({ onClose, onSaved }) {
  const [form, setForm] = useState({ username: "", password: "", permissions: [] });
  const [saving, setSaving] = useState(false);
  const toggle = (p) => setForm(f => ({ ...f, permissions: f.permissions.includes(p) ? f.permissions.filter(x => x !== p) : [...f.permissions, p] }));
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await usersApi.create(form);
      toast.success("تم إنشاء المستخدم");
      onSaved();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setSaving(false); }
  };
  return (
    <form data-testid="um-form" onSubmit={submit} className="bg-white border-2 border-slate-200 rounded-2xl p-5 space-y-4">
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-600" />
        <span className="text-xs text-slate-500">اسم المستخدم بأحرف صغيرة إنجليزية 3-32 حرفاً؛ كلمة المرور 12 حرفاً على الأقل.</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1">اسم المستخدم</label>
          <input data-testid="um-form-username" required minLength={3} maxLength={32} pattern="[a-z0-9][a-z0-9_.\-]{2,31}"
            value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value.toLowerCase() }))}
            className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold" dir="ltr" />
        </div>
        <div>
          <label className="block text-xs font-bold text-slate-500 mb-1">كلمة المرور</label>
          <input data-testid="um-form-password" required type="password" minLength={12} maxLength={128}
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
            className="w-full bg-slate-50 border-2 border-slate-200 rounded-lg px-3 py-2 font-bold" dir="ltr" />
        </div>
      </div>
      <div>
        <div className="text-xs font-bold text-slate-500 mb-2">الصلاحيات:</div>
        <div className="flex flex-wrap gap-2">
          {ALL_PERMS.map(p => (
            <button key={p.key} type="button" data-testid={`um-form-perm-${p.key}`} onClick={() => toggle(p.key)}
              className={`text-sm font-bold px-3 py-2 rounded-lg border-2 ${form.permissions.includes(p.key) ? "bg-emerald-50 border-emerald-300 text-emerald-900" : "bg-white border-slate-200 text-slate-600"}`}>
              {form.permissions.includes(p.key) ? "✓ " : ""}{p.label}
            </button>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} data-testid="um-form-cancel"
          className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 rounded-lg font-bold cursor-pointer">إلغاء</button>
        <button type="submit" disabled={saving} data-testid="um-form-submit"
          className="px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-bold disabled:opacity-50 cursor-pointer">
          {saving ? "..." : "حفظ"}
        </button>
      </div>
    </form>
  );
}
